import { Component, type ReactNode } from 'react'
import { EMAIL_CONTACT } from './constants'

const API_BASE_URL = (import.meta.env.VITE_API_URL || '') + '/api'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
  expanded: boolean
  reportSent: boolean
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null, expanded: false, reportSent: false }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo)
    this.setState({ errorInfo })

    // POST l'erreur au backend pour logging serveur (visible dans docker logs lobster-backend).
    // Fail-safe : si fetch échoue (offline, CORS, etc.), on swallow — on a déjà le console.error.
    try {
      fetch(`${API_BASE_URL}/errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: error?.message ?? 'unknown',
          name: error?.name ?? 'Error',
          stack: error?.stack?.slice(0, 4000) ?? '',
          component_stack: errorInfo?.componentStack?.slice(0, 4000) ?? '',
          url: typeof window !== 'undefined' ? window.location.href : '',
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          ts: new Date().toISOString(),
        }),
      }).then(() => this.setState({ reportSent: true })).catch(() => {})
    } catch {
      // ignore
    }
  }

  render() {
    if (this.state.hasError) {
      const stack = this.state.error?.stack || ''
      const compStack = this.state.errorInfo?.componentStack || ''
      const detailsText = `${this.state.error?.name || 'Error'}: ${this.state.error?.message || ''}\n\n` +
                          `--- JS stack ---\n${stack}\n\n--- Component stack ---\n${compStack}`

      return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 text-center py-8">
          <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-8 max-w-2xl w-full">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-brand-900 mb-2">
              {"Une erreur inattendue s'est produite"}
            </h1>
            <p className="text-brand-500 mb-2">
              {"Nous sommes désolés pour la gêne occasionnée. Veuillez réessayer."}
            </p>
            {this.state.reportSent && (
              <p className="text-xs text-green-600 mb-6">✓ Notre équipe a été notifiée automatiquement</p>
            )}
            {!this.state.reportSent && (
              <p className="text-xs text-brand-400 mb-6">Si le problème persiste, écrivez-nous à {EMAIL_CONTACT}</p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
              <button
                onClick={() => window.location.reload()}
                className="bg-accent-600 text-white font-medium rounded-xl px-6 py-3 hover:bg-accent-700 transition"
              >
                Recharger la page
              </button>
              <a
                href="/"
                className="border border-brand-200 text-brand-700 font-medium rounded-xl px-6 py-3 hover:bg-brand-50 transition"
              >
                {"Retour à l'accueil"}
              </a>
            </div>

            <button
              onClick={() => this.setState(s => ({ expanded: !s.expanded }))}
              className="text-xs text-brand-400 hover:text-brand-600 underline"
            >
              {this.state.expanded ? 'Masquer les détails techniques' : 'Afficher les détails techniques'}
            </button>

            {this.state.expanded && (
              <div className="mt-4 text-left">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-64 overflow-auto">
                  <pre className="text-[10px] text-gray-700 whitespace-pre-wrap break-words font-mono">{detailsText}</pre>
                </div>
                <button
                  onClick={() => navigator.clipboard?.writeText(detailsText)}
                  className="mt-2 text-xs text-accent-600 hover:underline"
                >
                  Copier les détails dans le presse-papier
                </button>
              </div>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
